import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography
} from '@mui/material';
import { Close } from '@mui/icons-material';
import './AccountHeadModal.css';

const AccountHeadModal = ({
  open,
  onClose,
  modalAction,
  selectedItem,
  formData,
  setFormData,
  onSave,
  loading,
  accountHeads = [],
  balanceSheetHeads = [],
  mainHeads = []
}) => {
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (selectedItem && modalAction !== 'create') {
      setFormData(selectedItem);
    } else if (modalAction === 'create') {
      setFormData(getDefaultFormData());
    }
  }, [selectedItem, modalAction, setFormData]);

  const getDefaultFormData = () => {
    return {
      Name: '',
      BalanceSheetHeadId: '',
      Description: '',
      IsActive: true,
      MainHeadId: null,
      VATRate: 0,
      SACCode: '',
      IsExpenseOrIncome: false,
      IsTrading: false
    };
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Helper to get value from either lowercase or PascalCase field
    const getValue = (lowercase, pascalCase) => formData[lowercase] ?? formData[pascalCase];
    
    if (!(getValue('name', 'Name')?.toString().trim())) {
      newErrors.name = 'Account name is required';
    }
    
    if (!(getValue('balanceSheetHeadId', 'BalanceSheetHeadId')?.toString().trim())) {
      newErrors.balanceSheetHeadId = 'Balance Sheet is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave();
    }
  };

  const getModalTitle = () => {
    const actionNames = {
      create: 'New',
      edit: 'Edit',
      view: 'View'
    };
    
    const actionName = actionNames[modalAction] || 'New';
    
    return {
      title: `${actionName} Account`,
      subtitle: `${modalAction === 'create' ? 'Add a new' : modalAction === 'edit' ? 'Update' : 'View'} account ${modalAction === 'create' ? 'to your chart of accounts' : 'details'}`
    };
  };


  const renderForm = () => (
    <div className="form-container">
      {/* Basic Information Section */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e3a8a', mb: 1, mt: 1 }}>
        Basic Information
      </Typography>
      
      <div className="form-row">
        <div className="form-field required-field">
          <TextField
            fullWidth
            label="Account Name"
            placeholder="Enter account name"
            value={formData.Name || formData.name || ''}
            onChange={(e) => handleInputChange('Name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            disabled={modalAction === 'view'}
            required
          />
        </div>
      </div>
      
      {/* Account Setup Section */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e3a8a', mb: 1, mt: 2 }}>
        Account Setup
      </Typography>
      
      <div className="form-row">
        <div className="form-field required-field">
          <FormControl fullWidth error={!!errors.balanceSheetHeadId} required>
            <InputLabel>Balance Sheet</InputLabel>
            <Select
              value={formData.BalanceSheetHeadId || formData.balanceSheetHeadId || ''}
              onChange={(e) => handleInputChange('BalanceSheetHeadId', e.target.value)}
              disabled={modalAction === 'view'}
              label="Balance Sheet *"
            >
              <MenuItem value="">
                <em>Select Balance Sheet</em>
              </MenuItem>
              {balanceSheetHeads.map(head => (
                <MenuItem key={head.Id} value={head.Id}>
                  {head.Name}
                </MenuItem>
              ))}
            </Select>
            {errors.balanceSheetHeadId && (
              <Typography variant="caption" color="error" sx={{ ml: 0, mt: 0.5 }}>
                {errors.balanceSheetHeadId}
              </Typography>
            )}
          </FormControl>
        </div>
        <div className="form-field">
          <FormControl fullWidth>
            <InputLabel>Parent Account (Optional)</InputLabel>
            <Select
              value={formData.MainHeadId || formData.mainHeadId || ''}
              onChange={(e) => handleInputChange('MainHeadId', e.target.value)}
              disabled={modalAction === 'view'}
              label="Parent Account (Optional)"
            >
              <MenuItem value="">
                <em>Create as Main Account</em>
              </MenuItem>
              {mainHeads.map(head => (
                <MenuItem key={head.Id} value={head.Id}>
                  {head.Name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#6b7280', 
              fontSize: '0.75rem',
              display: 'block',
              mt: 0.5,
              fontStyle: 'italic'
            }}
          >
            Leave empty for main account, select parent for sub-account
          </Typography>
        </div>
      </div>

      {/* GST and SAC Information */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e3a8a', mb: 1, mt: 2 }}>
        VAT & SAC Information
      </Typography>
      
      <div className="form-row">
        <div className="form-field">
          <TextField
            fullWidth
            label="VAT Rate (%)"
            type="number"
            placeholder="Enter VAT rate"
            value={formData.VATRate || formData.vatRate || 0}
            onChange={(e) => {
              const value = e.target.value;
              // Limit to 3 digits (0-100)
              if (value === '' || (parseFloat(value) >= 0 && parseFloat(value) <= 100)) {
                handleInputChange('VATRate', parseFloat(value) || 0);
              }
            }}
            disabled={modalAction === 'view'}
            inputProps={{ min: 0, max: 100, step: 0.01, maxLength: 6 }}
            sx={{ maxWidth: '290px' }}
          />
        </div>
        <div className="form-field">
          <TextField
            fullWidth
            label="SAC Code"
            placeholder="Enter SAC code (max 10 chars)"
            value={formData.SACCode || formData.sacCode || ''}
            onChange={(e) => handleInputChange('SACCode', e.target.value)}
            disabled={modalAction === 'view'}
            inputProps={{ maxLength: 10 }}
          />
        </div>
      </div>

      {/* Account Settings */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e3a8a', mb: 1, mt: 2 }}>
        Account Settings
      </Typography>
      
      <div className="form-row">
        {/* <div className="form-field">
          <FormControlLabel
            control={
              <Switch
                checked={formData.IsActive ?? formData.isActive ?? true}
                onChange={(e) => handleInputChange('IsActive', e.target.checked)}
                disabled={modalAction === 'view'}
                color="success"
              />
            }
            label="Active"
          />
        </div> */}
        <div className="form-field">
          <FormControlLabel
            control={
              <Switch
                checked={formData.IsExpenseOrIncome ?? formData.isExpenseOrIncome ?? false}
                onChange={(e) => handleInputChange('IsExpenseOrIncome', e.target.checked)}
                disabled={modalAction === 'view'}
                color="primary"
              />
            }
            // label="Expense/Income"
             label="Income/Expense"
          />
        </div>
        <div className="form-field">
          <FormControlLabel
            control={
              <Switch
                checked={formData.IsTrading ?? formData.isTrading ?? false}
                onChange={(e) => handleInputChange('IsTrading', e.target.checked)}
                disabled={modalAction === 'view'}
                color="secondary"
              />
            }
            label="Trading"
          />
        </div>
      </div>

      {/* Description Section */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e3a8a', mb: 1, mt: 2 }}>
        Additional Details
      </Typography>
      
      <div className="form-field">
        <TextField
          fullWidth
          label="Description"
          placeholder="Enter account description (optional, max 150 chars)"
          multiline
          rows={2}
          value={formData.Description || formData.description || ''}
          onChange={(e) => handleInputChange('Description', e.target.value)}
          disabled={modalAction === 'view'}
          inputProps={{ maxLength: 150 }}
          helperText={`${(formData.Description || formData.description || '').length}/150 characters`}
        />
      </div>
    </div>
  );

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      className="account-head-modal"
    >
      <DialogTitle>
        <div className="modal-title-container">
          <div className="modal-title-text">
            <h2>{getModalTitle().title}</h2>
          </div>
          <Button
            onClick={onClose}
            className="modal-close-btn"
          >
            <Close />
          </Button>
        </div>
      </DialogTitle>
      
      <DialogContent>
        {renderForm()}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        {modalAction !== 'view' && (
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {modalAction === 'create' ? 'Add Account' : 'Update'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AccountHeadModal;